import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Wallet, TrendingUp, ChevronLeft, Upload, X, FileSpreadsheet, Trash2 } from 'lucide-react';
import { FaTrash, FaEllipsisH, FaChartLine } from 'react-icons/fa';
import axiosInstance from '../../utils/axiosConfig';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import TableView from '../TableView';
import { QuickActionsMenu } from '../common';
import EditableTextCell from './EditableTextCell';
import CustomSelect from './CustomSelect';
import ClearMonthModal from './ClearMonthModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import TendenciaAnualModal from './TendenciaAnualModal';
import toast from 'react-hot-toast';
import { SURA_BLUE, SURA_BLUE2, SURA_GREEN, SURA_TEAL, mesesVisibles } from './palette';

const MESES_ORDEN = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-xl p-3 min-w-[140px]">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex justify-between gap-3 py-0.5">
          <span className="text-xs text-gray-600">Gasto</span>
          <span className="text-xs font-bold text-gray-900">${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      ))}
    </div>
  );
};

const PresupuestoComprasView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [concepto, setConcepto] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);

  const mesActual = MESES_ORDEN[new Date().getMonth()];
  const [mesSeleccionado, setMesSeleccionado] = useState(mesActual);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tasaBcv, setTasaBcv] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [facturaAEliminar, setFacturaAEliminar] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const [trendItem, setTrendItem] = useState(null); // { titulo, subtitulo, data }

  useEffect(() => { fetchData(); }, [id, mesSeleccionado, anioSeleccionado]); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const cRes = await axiosInstance.get('/api/presupuestos/conceptos');
      const found = cRes.data.find(c => c.id === parseInt(id));
      setConcepto(found);
      
      const res = await axiosInstance.get(`/api/presupuestos/compras/${id}`);
      setGastos(res.data || []);
    } catch (err) {
      console.error('Error fetching compras', err);
      toast.error('No se pudo cargar el historial de compras');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGasto = async (gastoId, field, value) => {
    try {
      await axiosInstance.put(`/api/presupuestos/compras/gasto/${gastoId}`, { [field]: value });
      setGastos(prev => prev.map(g => g.id === gastoId ? { ...g, [field]: value } : g));
      toast.success("Dato actualizado");
    } catch (error) {
      toast.error("Error al actualizar la compra");
    }
  };

  const handleDeleteGasto = async (gastoId) => {
    try {
      await axiosInstance.delete(`/api/presupuestos/compras/gasto/${gastoId}`);
      toast.success('Factura eliminada');
      setGastos(prev => prev.filter(g => g.id !== gastoId));
    } catch (error) {
      toast.error('Error al eliminar la factura');
    } finally {
      setFacturaAEliminar(null);
    }
  };

  const handleClearMonth = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmClearMonth = async () => {
    try {
      await axiosInstance.delete(`/api/presupuestos/clear/compras/${id}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`);
      toast.success(`Datos de ${mesSeleccionado} eliminados correctamente.`);
      setIsDeleteModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar los datos del mes');
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo Excel primero');
      return;
    }

    const formData = new FormData();
    formData.append('factura', selectedFile);
    formData.append('mes', mesSeleccionado);
    formData.append('anio', anioSeleccionado);
    if (tasaBcv && !isNaN(parseFloat(tasaBcv))) {
      formData.append('tasa_bcv', parseFloat(tasaBcv));
    }

    setUploading(true);
    const toastId = toast.loading('Procesando archivo Excel...');
    try {
      const { data } = await axiosInstance.post(`/api/presupuestos/compras/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchData();
      toast.success(`Archivo procesado correctamente. ${data.length} compras registradas.`, { id: toastId });
      setShowUploadModal(false);
      setSelectedFile(null);
      setTasaBcv('');
    } catch (error) {
      const msg = error.response?.data?.error || "Error al procesar el archivo Excel";
      toast.error(`Error: ${msg}`, { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      header: 'Número Factura',
      accessorKey: 'numero_factura',
      cell: ({ row }) => (
        <div className="font-mono text-sm text-gray-800 w-32 px-1">
          {row.original.numero_factura || '—'}
        </div>
      )
    },
    {
      header: 'Fecha Emisión',
      accessorKey: 'fecha_emision',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600 w-32">
          <EditableTextCell
            value={row.original.fecha_emision}
            onSave={(val) => handleUpdateGasto(row.original.id, 'fecha_emision', val)}
            placeholder="YYYY-MM-DD"
          />
        </div>
      )
    },
    {
      header: 'Proveedor',
      accessorKey: 'proveedor',
      cell: ({ row }) => (
        <div className="font-semibold text-gray-800 text-sm w-48 px-1">
          {row.original.proveedor || '—'}
        </div>
      )
    },
    {
      header: 'Descripción',
      accessorKey: 'descripcion',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600 min-w-[200px]">
          <EditableTextCell
            value={row.original.descripcion}
            onSave={(val) => handleUpdateGasto(row.original.id, 'descripcion', val)}
            placeholder="Descripción de la compra..."
          />
        </div>
      )
    },
    {
      header: 'Subtotal (Bs)',
      accessorKey: 'subtotal_bs',
      cell: ({ row }) => (
        <div className="font-mono text-sm tabular-nums text-gray-500 px-1">
          {row.original.subtotal_bs != null ? `Bs. ${Number(row.original.subtotal_bs).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
        </div>
      )
    },
    {
      header: 'Subtotal ($)',
      accessorKey: 'subtotal',
      cell: ({ row }) => (
        <div className="font-mono font-bold tabular-nums text-sm px-1" style={{ color: SURA_BLUE }}>
          {row.original.subtotal != null ? `$${Number(row.original.subtotal).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
        </div>
      )
    },
    {
      id: 'acciones', header: '', size: 50,
      cell: ({ row }) => (
        <div className="quick-actions-container" style={{ position: 'relative' }}>
          <button
            className={`icon-button ${activeActionMenu === row.id ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (activeActionMenu === row.id) {
                setActiveActionMenu(null);
                setMenuAnchorRect(null);
              } else {
                setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                setActiveActionMenu(row.id);
              }
            }}
          >
            <FaEllipsisH color="#111827" />
          </button>
          {activeActionMenu === row.id && (
            <QuickActionsMenu anchorRect={menuAnchorRect}>
              {[
                { icon: FaChartLine, label: 'Ver tendencia anual', color: SURA_BLUE2, onClick: () => {
                    const key = (row.original.proveedor || '').trim();
                    const serie = mesesVisibles(anioSeleccionado).map(mes => {
                      const items = gastos.filter(g => g.anio === anioSeleccionado && g.mes === mes && (g.proveedor || '').trim() === key);
                      if (!items.length) return null;
                      return { name: mes.substring(0, 3), fullMonth: mes, total: items.reduce((s, g) => s + Number(g.subtotal || 0), 0) };
                    }).filter(Boolean);
                    setTrendItem({ titulo: row.original.proveedor || 'Proveedor', subtitulo: row.original.numero_factura || '', data: serie });
                    setActiveActionMenu(null);
                  } },
                { icon: FaTrash, label: 'Eliminar Factura', color: '#EF4444', onClick: () => { setFacturaAEliminar(row.original.id); setActiveActionMenu(null); } }
              ].map((action) => (
                <button key={action.label} className="quick-action-item" onClick={action.onClick}>
                  <span className="action-icon" style={{ color: action.color }}><action.icon /></span>
                  <span className="action-label">{action.label}</span>
                </button>
              ))}
            </QuickActionsMenu>
          )}
        </div>
      )
    }
  ];



  // Total filtered by selected month and year
  const totalMes = useMemo(() => {
    return gastos
      .filter(g => g.mes === mesSeleccionado && g.anio === anioSeleccionado)
      .reduce((sum, g) => sum + (g.subtotal || 0), 0);
  }, [gastos, mesSeleccionado, anioSeleccionado]);
  
  const totalFacturasMes = useMemo(() => {
    return gastos.filter(g => g.mes === mesSeleccionado && g.anio === anioSeleccionado).length;
  }, [gastos, mesSeleccionado, anioSeleccionado]);

  // Gráfica anual: solo meses ya transcurridos (sin meses futuros).
  const chartData = useMemo(() => {
    return mesesVisibles(anioSeleccionado).map(mes => {
      const total = gastos
        .filter(g => g.mes === mes && g.anio === anioSeleccionado)
        .reduce((sum, g) => sum + (g.subtotal || 0), 0);
      return { name: mes.substring(0, 3), fullMonth: mes, total };
    });
  }, [gastos, anioSeleccionado]);

  if (loading) {
    return <div className="p-8 text-center text-gray-500 font-medium text-shimmer">Cargando datos de Compras...</div>;
  }

  // Filter the table to only show the selected month/year
  const gastosFiltrados = gastos.filter(g => g.mes === mesSeleccionado && g.anio === anioSeleccionado);

  return (
    <div className="assets-view">

      {/* ── Hero Header ──────────────────────────────────────────── */}
      <div
        className="rounded-2xl mb-6 px-7 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative"
        style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_BLUE2} 100%)` }}
      >
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute right-0 top-0 w-48 h-48 rounded-full opacity-10" style={{ background: '#fff', transform: 'translate(30%, -30%)' }} />
          <div className="absolute right-12 bottom-0 w-32 h-32 rounded-full opacity-5" style={{ background: '#fff', transform: 'translateY(40%)' }} />
        </div>

        <div className="flex items-center gap-4 z-10">
          <button onClick={() => navigate('/presupuestos')} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-medium transition-colors">
            <ChevronLeft size={16} /> Presupuestos
          </button>
          <div className="w-px h-5 bg-white/20" />
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
            <ShoppingCart size={22} color="#fff" />
          </div>
          <div>
            <h2 className="text-white text-xl font-bold m-0 leading-tight">{concepto?.nombre || 'Compras'}</h2>
            <p className="text-white/60 text-xs m-0 mt-0.5">Gestión y carga de facturas de compras</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 z-10">
          <CustomSelect
            value={mesSeleccionado}
            onChange={(e) => setMesSeleccionado(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            {MESES_ORDEN.map(m => <option key={m} value={m}>{m}</option>)}
          </CustomSelect>
          <CustomSelect
            value={anioSeleccionado}
            onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
            className="px-3 py-1.5 rounded-lg text-sm font-medium outline-none"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </CustomSelect>
          <button
            onClick={handleClearMonth}
            className="bg-red-500/20 hover:bg-red-500 text-white p-1.5 rounded-lg transition-colors border border-red-500/30 hover:border-red-500 flex items-center justify-center group"
            title="Limpiar datos del mes"
          >
            <Trash2 size={16} className="text-red-300 group-hover:text-white transition-colors" />
          </button>
          <span
            className="text-xs font-semibold px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
          >
            COMPRAS
          </span>
        </div>
      </div>

      {/* ── KPI Row ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[
          { title: 'Facturas del Mes', value: totalFacturasMes, icon: ShoppingCart, color: SURA_BLUE, bg: '#EFF6FF', mono: false },
          { title: 'Gasto Total del Mes', value: `$${totalMes.toFixed(2)}`, icon: Wallet, color: SURA_GREEN, bg: '#ECFDF5', mono: true },
        ].map(kpi => (
          <div key={kpi.title} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow">
            <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: kpi.color }} />
            <div className="flex justify-between items-start mb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{kpi.title}</p>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: kpi.bg }}>
                <kpi.icon size={17} style={{ color: kpi.color }} />
              </div>
            </div>
            <p className={`text-2xl font-bold text-gray-900 ${kpi.mono ? 'font-mono' : ''}`}>{kpi.value}</p>
          </div>
        ))}

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_TEAL }} />
          <div className="flex justify-between items-center mb-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tendencia Anual</p>
            <TrendingUp size={14} style={{ color: SURA_TEAL }} />
          </div>
          <div className="h-16 w-full mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradCompras" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SURA_TEAL} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={SURA_TEAL} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" stroke={SURA_TEAL} strokeWidth={2} fill="url(#gradCompras)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            Facturas de {mesSeleccionado} {anioSeleccionado}
          </h3>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 text-sm font-bold text-white rounded-lg flex items-center gap-2 shadow-sm transition-all transform active:scale-95"
              style={{ background: SURA_BLUE }}
            >
              <Upload size={16} />
              Subir Facturas
            </button>
          </div>
        </div>
        <TableView columns={columns} data={gastosFiltrados} searchPlaceholder="Buscar factura por proveedor o descripción..." />
      </div>

      {/* MODAL PARA SUBIR EXCEL */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" style={{ borderTop: `4px solid ${SURA_BLUE2}` }}>
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                <Upload size={20} style={{ color: SURA_BLUE2 }} />
                Subir Facturas
              </h3>
              <button onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tasa de Cambio BCV</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-500 font-bold">Bs</span>
                  <input 
                    type="number"
                    placeholder="Ej. 36.50"
                    value={tasaBcv}
                    onChange={(e) => setTasaBcv(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition-all shadow-sm"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">Requerido para convertir el Subtotal a Dólares.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Archivo Excel</label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/50 hover:border-blue-200 transition-all group"
                >
                  <FileSpreadsheet size={32} className="text-gray-300 group-hover:text-blue-500 mb-3 transition-colors" />
                  {selectedFile ? (
                    <span className="text-sm font-medium text-gray-700">{selectedFile.name}</span>
                  ) : (
                    <>
                      <span className="text-sm font-medium text-gray-600">Haz clic para seleccionar</span>
                      <span className="text-xs text-gray-400 mt-1">.xlsx, .xls</span>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  accept=".xlsx, .xls" 
                  className="hidden" 
                  ref={fileInputRef}
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                />
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => { setShowUploadModal(false); setSelectedFile(null); }}
                className="px-5 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                disabled={uploading || !selectedFile || !tasaBcv}
                onClick={handleUploadSubmit}
                className="px-5 py-2 text-sm font-bold text-white rounded-lg shadow-sm transition-all transform active:scale-95 disabled:opacity-50 disabled:active:scale-100 flex items-center gap-2"
                style={{ background: SURA_BLUE }}
              >
                {uploading ? 'Subiendo...' : 'Subir Archivo'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ClearMonthModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmClearMonth}
        mes={mesSeleccionado}
        anio={anioSeleccionado}
        tipo="Compras"
      />

      <ConfirmDeleteModal
        isOpen={facturaAEliminar !== null}
        onClose={() => setFacturaAEliminar(null)}
        onConfirm={() => handleDeleteGasto(facturaAEliminar)}
        title="Eliminar factura"
        message="¿Seguro que deseas eliminar esta factura?"
      />

      <TendenciaAnualModal
        isOpen={trendItem !== null}
        onClose={() => setTrendItem(null)}
        titulo={trendItem?.titulo}
        subtitulo={trendItem?.subtitulo}
        anio={anioSeleccionado}
        data={trendItem?.data || []}
      />
    </div>
  );
};

export default PresupuestoComprasView;
